import React, { useState, useMemo, useEffect } from "react";
import { useQueryClient } from '@tanstack/react-query';
import css from "../style.module.css";
import QuestionItem from "./QuestionItem";
import SkeletonQuestionsPage from "../../LoadingPages/SkeletonQuestionsPage";
import IncompleteModal from "../Popup Modals/IncompleteModal";
import SubmitConfirm from "../Popup Modals/SubmitConfirm";
import SavingOverlay from "../Popup Modals/SavingOverlay";
import { VendorWarning } from "../Popup Modals/VendorWarning";
import { QuestionnaireCompleted } from "../Popup Modals/QuestionnaireCompleted";
import { fileSchema } from "../../../lib/Yup Validation/VendorsSchemas";

export default function Questions({
    questionnaire, questionnaireId, Audits_r, pirm_addon__Status__c,
    allQuestions, handleClick, questionnaireLength,
    position, onUpdateAnswer, onUploadFile, onDeleteFile, isSaving
}) {
    const queryClient = useQueryClient();
    const [submitTriggered, setSubmitTriggered] = useState(false);
    const [highlightedQuestions, setHighlightedQuestions] = useState(new Set());
    const [unansweredQuestions, setUnansweredQuestions] = useState([]);
    const [showIncompleteModal, setShowIncompleteModal] = useState(false);
    const [showFinalConfirm, setShowFinalConfirm] = useState(false);
    const [isSuccessVisible, setIsSuccessVisible] = useState(false);
    const [isActive, setIsActive] = useState(false);

    const [fileName, setFileName] = useState({});
    const [deletingFiles, setDeletingFiles] = useState(new Set());
    const [deletedFiles, setDeletedFiles] = useState(new Set());
    const [fileErrors, setFileErrors] = useState({});

    const isReadOnly = pirm_addon__Status__c === "Completed";

    const currentCacheData = queryClient.getQueryData(['questions', questionnaireId]) || allQuestions;

    // ✅ إضافة: مراقبة الإجابات وإزالة الـ highlight تلقائياً
    useEffect(() => {
        if (submitTriggered && highlightedQuestions.size > 0) {
            const stillUnanswered = new Set();

            highlightedQuestions.forEach(questionId => {
                const question = currentCacheData.find(q => q.Id === questionId);

                if (question) {
                    const isStillUnanswered =
                        question.pirm_addon__QuestionType__c === "Document Upload"
                            ? !(question.ContentDocumentLinks?.records?.length > 0)
                            : !(question.pirm_addon__Answer__c &&
                                question.pirm_addon__Answer__c !== "[]" &&
                                String(question.pirm_addon__Answer__c).trim() !== "");

                    if (isStillUnanswered) {
                        stillUnanswered.add(questionId);
                    }
                }
            });

            if (stillUnanswered.size !== highlightedQuestions.size) {
                setHighlightedQuestions(stillUnanswered);

                if (stillUnanswered.size === 0) {
                    setSubmitTriggered(false);
                    setShowIncompleteModal(false);
                }
            }
        }
    }, [currentCacheData, highlightedQuestions, submitTriggered]);

    const cleanStr = (str) => {
        if (!str) return [];
        return String(str)
            .replace(/[\[\]"']/g, '')
            .split(',')
            .map(s => s.trim())
            .filter(x => x !== "");
    };

    const checkIsShown = (q, currentState) => {
        if (!q.pirm_addon__Question__r?.pirm_addon__Related_Question__c) return true;
        const parentId = q.pirm_addon__Question__r.pirm_addon__Related_Question__c;
        const requiredAnswers = cleanStr(q.pirm_addon__Question__r.pirm_addon__Related_Answers__c);
        const parentQuestion = currentState.find(item => item.pirm_addon__Question__c === parentId);
        if (!parentQuestion || !parentQuestion.pirm_addon__Answer__c) return false;
        const currentAnswers = cleanStr(parentQuestion.pirm_addon__Answer__c);
        return requiredAnswers.some(req => currentAnswers.includes(req));
    };

    const buildSyncPayload = (currentQuestions, targetId = null, targetVal = null) => {
        const safeQuestions = currentQuestions || [];
        const tempState = safeQuestions.map(item =>
            item.Id === targetId ? { ...item, pirm_addon__Answer__c: targetVal } : item
        );

        return {
            newAnswer: tempState.map(q => ({
                recordId: q.Id,
                newAnswer: q.pirm_addon__Answer__c || null,
                isApplicable: checkIsShown(q, tempState)
            }))
        };
    };

    const updateLocalCache = (questionId, newValue) => {
        queryClient.setQueryData(['questions', questionnaireId], (oldData) => {
            if (!oldData) return oldData;
            const updatedWithNewAnswer = oldData.map(q =>
                q.Id === questionId ? { ...q, pirm_addon__Answer__c: newValue } : q
            );
            return updatedWithNewAnswer.map(q => ({
                ...q,
                pirm_addon__IsApplicable__c: checkIsShown(q, updatedWithNewAnswer)
            }));
        });
    };

    const stats = useMemo(() => {
        const visibleNonBlockers = currentCacheData.filter(q =>
            q.pirm_addon__IsApplicable__c &&
            q.pirm_addon__QuestionType__c !== "Blocker"
        );
        const total = visibleNonBlockers.length;
        const answered = visibleNonBlockers.filter(q => {
            if (q.pirm_addon__QuestionType__c === "Document Upload") {
                return q.ContentDocumentLinks?.records?.length > 0;
            }
            return (
                q.pirm_addon__Answer__c &&
                q.pirm_addon__Answer__c !== "[]" &&
                String(q.pirm_addon__Answer__c).trim() !== ""
            );
        }).length;
        return { total, answered };
    }, [currentCacheData]);

    const handlePickListUpdate = async (q, val) => {
        const newValue = `["${val}"]`;
        updateLocalCache(q.Id, newValue);

        const latestCache = queryClient.getQueryData(['questions', questionnaireId]) || allQuestions;
        const payload = buildSyncPayload(latestCache, q.Id, newValue);

        await onUpdateAnswer({ auditId: Audits_r, action: "not updated", payload });
    };

    const handleMultiSelectChange = (q, val) => {
        const curr = cleanStr(q.pirm_addon__Answer__c);
        const next = curr.includes(val)
            ? curr.filter(v => v !== val)
            : [...curr, val];
        const newValue = JSON.stringify(next);

        updateLocalCache(q.Id, newValue);
    };

    const handleTextChange = (q, val) => {
        updateLocalCache(q.Id, val);
    };

    const handleFileChange = async (e, questionId) => {
        const file = e.target.files[0];
        if (!file) return;
        try {
            await fileSchema.validate({ file: file });
            setFileErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[questionId];
                return newErrors;
            });
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = async () => {
                const base64 = reader.result.split(',')[1];
                setFileName(prev => ({ ...prev, [questionId]: file.name }));
                await onUploadFile({ files: [{ recordId: questionId, fileName: file.name, fileContent: base64 }] });
                await onUpdateAnswer({ auditId: Audits_r, action: "not updated", payload: buildSyncPayload(currentCacheData) });
            };
        } catch (err) {
            setFileErrors(prev => ({ ...prev, [questionId]: err.message }));
            e.target.value = null;
        }
    };

    const handleFileDelete = async (question, fileId) => {
        setDeletingFiles(prev => new Set([...prev, fileId]));
        try {
            await onDeleteFile(fileId);
            setFileName(prev => { const n = { ...prev }; delete n[question.Id]; return n; });
            await onUpdateAnswer({ auditId: Audits_r, action: "not updated", payload: buildSyncPayload(currentCacheData) });
            setDeletedFiles(prev => new Set([...prev, fileId]));
            setDeletingFiles(prev => { const n = new Set(prev); n.delete(fileId); return n; });
            setTimeout(() => setDeletedFiles(prev => { const n = new Set(prev); n.delete(fileId); return n; }), 2000);
        } catch (error) {
            setDeletingFiles(prev => { const n = new Set(prev); n.delete(fileId); return n; });
        }
    };

    const handleSaveDraft = async () => {
        await onUpdateAnswer({ auditId: Audits_r, action: "not updated", payload: buildSyncPayload(currentCacheData) });
    };

    const handleFinalSubmitAction = async () => {
        setShowFinalConfirm(false);
        try {
            await onUpdateAnswer({ auditId: Audits_r, action: "updated", payload: buildSyncPayload(currentCacheData) });
            setIsActive(true);
            setIsSuccessVisible(true);
        } catch (error) {
            console.error("Submit failed", error);
        }
    };

    const handleFinalSubmitClick = () => {
        const unanswered = currentCacheData.filter(q =>
            q.pirm_addon__IsApplicable__c &&
            q.pirm_addon__QuestionType__c !== "Blocker" &&
            !(q.pirm_addon__Answer__c && q.pirm_addon__Answer__c !== "[]") &&
            !(q.ContentDocumentLinks?.records?.length > 0)
        );
        if (unanswered.length > 0) {
            setSubmitTriggered(true);
            setUnansweredQuestions(unanswered);
            setHighlightedQuestions(new Set(unanswered.map(q => q.Id)));
            setShowIncompleteModal(true);
            setTimeout(() => {
                document.querySelector(`[data-question-id="${unanswered[0].Id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 100);
        } else {
            setShowFinalConfirm(true);
        }
    };

    if (!questionnaire) return <SkeletonQuestionsPage />;
    if (questionnaire.length === 0) return <VendorWarning />;

    return (
        <div className="ps-5">
            {currentCacheData
                .filter(q => questionnaire.some(item => item.Id === q.Id))
                .filter(q => q.pirm_addon__IsApplicable__c)
                .map((question) => (
                    <QuestionItem
                        key={question.Id}
                        question={question}
                        isReadOnly={isReadOnly}
                        submitTriggered={submitTriggered}
                        highlightedQuestions={highlightedQuestions}
                        fileName={fileName}
                        deletingFiles={deletingFiles}
                        deletedFiles={deletedFiles}
                        onPickListClick={handlePickListUpdate}
                        onMultiSelectChange={handleMultiSelectChange}
                        onTextChange={handleTextChange}
                        onFileChange={handleFileChange}
                        fileError={fileErrors[question.Id]}
                        onFileDelete={handleFileDelete}
                    />
                ))}

            <div className="d-flex align-items-center mt-5">
                <div className="d-flex">
                    {position > 0 && <button className="b-pink-color me-2" onClick={() => handleClick(position - 1)}>Prev Section</button>}
                    {position < questionnaireLength - 1 && <button className="b-pink-color me-2" onClick={() => handleClick(position + 1)}>Next Section</button>}
                </div>
                {!isReadOnly && (
                    <div className="d-flex align-items-center">
                        <button className="btn btn-success px-4" onClick={handleFinalSubmitClick}>Submit</button>
                        <p onClick={handleSaveDraft} className="m-0 ms-2 holdCursor text-primary" style={{ textDecoration: 'underline', fontWeight: 'normal' }}>Save Draft</p>
                    </div>
                )}
            </div>

            <p className="fw-semibold text-dark mt-3">
                Total Answered Questions: {stats.answered} / {stats.total}
            </p>

            {showIncompleteModal && <IncompleteModal setShowIncompleteModal={setShowIncompleteModal} unansweredQuestions={unansweredQuestions} />}
            {showFinalConfirm && <SubmitConfirm setShowFinalConfirm={setShowFinalConfirm} onConfirm={handleFinalSubmitAction} />}
            {(isSuccessVisible && !isSaving) && (
                <QuestionnaireCompleted
                    isActive={isActive}
                    closeModal={() => {
                        setIsActive(false);
                        setIsSuccessVisible(false);
                    }}
                />
            )}
            {isSaving && <SavingOverlay />}
        </div>
    );
}